import { useState, useEffect } from "react";
import { Mail, ShoppingCart, Calendar } from "lucide-react";
import axios from "axios";
import AdminLayout from "../../components/AdminLayout";
import { API } from "../../App";
import { format } from "date-fns";
import { motion } from "framer-motion";

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get(`${API}/admin/customers`, { withCredentials: true });
        setCustomers(response.data);
      } catch (error) {
        console.error("Error fetching customers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-customers">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Customers</h1>
          <p className="text-muted-foreground mt-1">View and manage customer accounts</p>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-card rounded-xl"></div>
            ))}
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-20 bg-card border border-white/5 rounded-xl">
            <p className="text-muted-foreground">No customers found</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-card border border-white/5 rounded-xl overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Email</th>
                    <th>Orders</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.user_id} data-testid={`customer-row-${customer.user_id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          {customer.picture ? (
                            <img 
                              src={customer.picture} 
                              alt={customer.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-primary font-medium">{customer.name?.[0]}</span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-foreground">{customer.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{customer.user_id}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="w-4 h-4" />
                          {customer.email}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-foreground">{customer.order_count || 0}</span>
                          <span className="text-muted-foreground">orders</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {formatDate(customer.created_at)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Stats */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-white/5 rounded-xl p-6"
          >
            <p className="text-sm text-muted-foreground">Total Customers</p>
            <p className="text-3xl font-bold text-foreground mt-1">{customers.length}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-white/5 rounded-xl p-6"
          >
            <p className="text-sm text-muted-foreground">With Orders</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {customers.filter(c => c.order_count > 0).length}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-white/5 rounded-xl p-6"
          >
            <p className="text-sm text-muted-foreground">Avg Orders/Customer</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {customers.length > 0 
                ? (customers.reduce((sum, c) => sum + (c.order_count || 0), 0) / customers.length).toFixed(1)
                : 0
              }
            </p>
          </motion.div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCustomers;
